
/* eslint-disable max-classes-per-file */
import { Injectable, inject } from "@angular/core";
import moment from "moment";
import { BehaviorSubject, Observable, Subject, firstValueFrom } from "rxjs";
import { MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { AnvandareDTO } from "~app/common/models/anvandare/AnvandareDTO";
import { SortimentDTO } from "~app/common/models/common/SortimentDTO";
import { Arbetsordertyper, Operationstyper } from "~app/common/models/constants";
import { ArbetsorderInmatningshandelssortimentDTO } from "~app/common/models/planera/ArbetsorderInmatningshandelssortimentDTO";
import { KontraktsradInfoDTO } from "~app/common/models/planera/KontraktsradInfoDTO";
import { OperationInfoForSchedulerDTO } from "~app/common/models/planera/OperationInfoForSchedulerDTO";
import { ArbetsorderHandelssortimentDTO } from "~app/common/models/planera/v2/ArbetsorderHandelssortimentDTO";
import { DestineringsalternativDTO } from "~app/common/models/planera/v2/DestineringsalternativDTO";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { ApiClientService } from "~app/common/services/api-client.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { FormatService } from "~app/common/services/format.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IArbetsorder } from "~app/features/planera/model/IArbetsorder";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IOperation } from "~app/features/planera/model/IOperation";
import { IOperationBase } from "~app/features/planera/model/IOperationBase";
import { ArbetsorderAttributEventMessage } from "../events/ArbetsorderAttributEventMessage";
import { IArbetsorderRestriktion } from "../model/IArbetsorderRestriktion";
import { Viol3DestineringsraderDto } from "../model/IViol3DestineringDto";

@Injectable({
    providedIn: "root"
})
export class ArbetsorderService {

    allaKontraktsrader$: BehaviorSubject<KontraktsradInfoDTO[]>;
    antalVirkesorderRader$: BehaviorSubject<number>;
    direktutbyteStatus$: BehaviorSubject<any>;

    private readonly formatService = inject(FormatService);
    private readonly apiClient = inject(ApiClientService);
    private readonly eventService = inject(EventService);

    constructor() {
        this.allaKontraktsrader$ = new BehaviorSubject<KontraktsradInfoDTO[]>([]);
        this.antalVirkesorderRader$ = new BehaviorSubject<number>(0);
        this.direktutbyteStatus$ = new BehaviorSubject<any>([]);
    }

    getOperationInArbetsorder(operationer: IOperationBase[], operationstyp: string): IOperationBase {
        if (!operationer || operationer.length === 0) {
            return undefined;
        }

        const operation = operationer.filter(p => p.operationstyp === operationstyp);

        return operation[0];
    }

    isSchemalagd(periodsattPeriod: string, operationer: IOperationBase[]): boolean {
        return !this.isPeriodsatt(periodsattPeriod) && !!operationer.length;
    }

    isPeriodsatt(periodsattPeriod: string): boolean {
        return ObjectUtils.hasValue(periodsattPeriod);
    }

    getArbetsorderOperationPeriod(arbetsorder: IArbetsorder): string {
        if (this.isPeriodsatt(arbetsorder.periodsattPeriod)) {
            return this.formatService.dateFormatRask(arbetsorder.periodsattPeriod, true);
        }

        if (!arbetsorder.operationer || arbetsorder.operationer.length <= 0) {
            return arbetsorder.avverkningFarStartaDatum ? this.formatService.dateFormatRask(arbetsorder.avverkningFarStartaDatum, false) : "";
        }

        const operationstyp = this.getOperationstypByArbetsordertyp(arbetsorder);
        const operation = this.getOperationInArbetsorder(arbetsorder.operationer, operationstyp);

        if (!operation) {
            return "";
        }

        return this.formatService
            .dateFormatRange(operation.startdatum, moment(operation.slutdatum).subtract(1, "d").toISOString());
    }

    getOperationByOperationstyp(arbetsorder: IArbetsorderDetail, operationstyp: string): IOperation {
        return arbetsorder.operationer.filter(p => p.operationstyp === operationstyp)[0];
    }

    getOperationstypByArbetsordertyp(arbetsorder: IArbetsorder): string {
        switch (arbetsorder.typ) {
            case Arbetsordertyper.IngenProduktion:
                return Operationstyper.Skotning;
            case Arbetsordertyper.Grotskotning:
                return Operationstyper.Grotskotning;
            default:
                return Operationstyper.Skordning;
        }
    }

    createOperation(
        arbetsorder: IArbetsorderDetail,
        operationstyp: string,
        startdatum: string,
        slutdatum: string): IOperation {
        const operation = {
            arbetsorderAffarsform: arbetsorder.affarsform,
            arbetsorderAnskaffningsform: arbetsorder.anskaffningsform,
            arbetsorderBeteckning: arbetsorder.beteckning,
            arbetsorderId: arbetsorder.id,
            arbetsorderRowVersion: arbetsorder.rowVersion,
            id: null,
            namn: arbetsorder.namn,
            operationstyp: operationstyp,
            slutdatum: slutdatum,
            startdatum: startdatum,
            typ: arbetsorder.avverkningsgrupp,
            valsSynkroniseringsstatus: undefined
        };
        arbetsorder.operationer.push(operation as IOperation);
        return operation as IOperation;
    }

    removeOperation(arbetsorder: IArbetsorderDetail, operation: IOperation): void {
        const index = arbetsorder.operationer.indexOf(operation);
        arbetsorder.operationer.splice(index, 1);
    }

    hasAtgardAndelData(obj: any) {
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (this.isNullOrEmpty(obj.andelTall)
            && this.isNullOrEmpty(obj.andelGran)
            && this.isNullOrEmpty(obj.andelLov)
            && this.isNullOrEmpty(obj.andelContorta)) {
            return false;
        }
        return true;
    }

    getOperationInfoForScheduler(arbetsorderId: number) {
        return firstValueFrom(this.apiClient.get<OperationInfoForSchedulerDTO>("Planera/GetOperationInfoForScheduler",
            false, { arbetsorderId: arbetsorderId }));
    }

    getArbetsorderRestriktion(id: number): Observable<IArbetsorderRestriktion> {
        return this.apiClient.get<IArbetsorderRestriktion>(`Planera/ArbetsorderRestriktion`, false, { restriktionId: id });
    }

    refreshKontraktsrader(arbetsorderId: string, destineringsalternativId: number): void {
        this.allaKontraktsrader$.next([]);
        this.apiClient.get<KontraktsradInfoDTO[]>("Planera/GetKontraktsrader", false, {
            arbetsorderId: arbetsorderId,
            destineringId: destineringsalternativId
        }).subscribe(x => {
            this.allaKontraktsrader$.next(x);
        });
    }

    getViol3Destineringsrader(arbetsorderId: number, destineringId?: number) {
        return firstValueFrom(this.apiClient.get<Viol3DestineringsraderDto>("Planera/GetViol3Destineringsrader",
            false,
            {
                arbetsorderId: arbetsorderId,
                destineringId: destineringId
            })
        );
    }

    getExtraSortiment(destineringsId: number): Observable<SortimentDTO[]> {
        return this.apiClient.get<SortimentDTO[]>("Planera/GetExtraSortiment/", false, { destineringsId: destineringsId });
    }

    emitAttributEvents(arbetsorderId: number): void {
        this.apiClient.get<ArbetsorderAttributEventMessage[]>(`Arbetsorder/${arbetsorderId}/attribut`).subscribe(response => {
            response.forEach(event => {
                this.eventService.emit(event);
            });
        });
    }

    getDestineringsalternativ(arbetsorderId: number, destineringId?: number): Observable<GetArbetsorderDestineringsalternativQueryResult> {
        let url = `arbetsorder/${arbetsorderId}/destineringsalternativ`;
        if (destineringId) {
            url = `${url}/${destineringId}`;
        }
        return this.apiClient.get<GetArbetsorderDestineringsalternativQueryResult>(url);
    }

    getDestineringsalternativManuella(arbetsorderId: number): Observable<GetArbetsorderDestineringsalternativManuellaQueryResult> {
        const url = `arbetsorder/${arbetsorderId}/destineringsalternativmanuella`;
        return this.apiClient.get<GetArbetsorderDestineringsalternativManuellaQueryResult>(url);
    }

    getArbetsorderAnsvarigAvverkningsledareTypeAhead(input: string): Observable<ITypeAheadResult[]> {
        return this.apiClient.get<ITypeAheadResult[]>("Planera/SearchForAvverkningledareTypeAhead", false, { input: input });
    }

    getArbetsorderHandelssortiment(arbetsorderId: number, arbetsorderHandelssortimentId?: number): Observable<GetArbetsorderHandelssortimentQueryResult> {
        let url = `arbetsorder/${arbetsorderId}/arbetsorderhandelssortiment`;
        if (arbetsorderHandelssortimentId) {
            url = `${url}/${arbetsorderHandelssortimentId}`;
        }
        return this.apiClient.get<GetArbetsorderHandelssortimentQueryResult>(url);
    }

    getArbetsorderInmatningshandelssortimentsrader(arbetsorderId: number) {
        return this.apiClient.get<ArbetsorderInmatningshandelssortimentDTO[]>("Planera/GetArbetsorderInmatningshandelssortimentsrader",
            false, { arbetsorderId: arbetsorderId }
        );
    }

    refreshAntalVirkesorderrader(arbetsorderId: number) {
        const url = `Planera/GetAntalVirkesorderrader/${arbetsorderId}`;
        this.apiClient.get<number>(url, true)
            .subscribe(x => {
                this.antalVirkesorderRader$.next(x);
            });
    }

    refreshDirektutbyteStatus(arbetsorderId: number) {
        const url = `Planera/GetDirektutbyteStatus/${arbetsorderId}`;
        this.apiClient.get<number>(url, true)
            .subscribe(x => {
                this.direktutbyteStatus$.next(x);
            });
    }

    getAnvandare(userName: string): Promise<AnvandareDTO> {
        return firstValueFrom(this.apiClient.get<AnvandareDTO>("Planera/GetAnvandare", false, { userName: userName }));
    }

    getArbetsorderErrors(userId: number): Observable<GetArbetsorderAlertsQueryResult[]> {
        return this.apiClient.get<GetArbetsorderAlertsQueryResult[]>(`arbetsordererror/${userId}`, false);
    }

    searchSortimentTypeAhead(input: string, excludeSste: string[], minLength = 0): Observable<ITypeAheadResult[]> {
        const subject = new Subject<ITypeAheadResult[]>();
        if (input.length < 3) {
            return subject.asObservable();
        }
        return this.apiClient
            .get<ITypeAheadResult[]>(`TypeAhead/sortiment?kod=${encodeURIComponent(input)}&excludeSste=${excludeSste.toString()}&minKodLength=${minLength}`, false);
    }

    getAlerts(arbetsorderId: number): Observable<GetArbetsorderAlertsQueryResult[]> {
        return this.apiClient
            .get<GetArbetsorderAlertsQueryResult[]>(`arbetsordererror/arbetsorder/${arbetsorderId}`, false);
    }

    private isNullOrEmpty(variable: any): boolean {
        return variable === null || variable === undefined || variable === "";
    }
}

export class GetArbetsorderDestineringsalternativQueryResult {
    destineringsalternativ: DestineringsalternativDTO[];

}

export class GetArbetsorderDestineringsalternativManuellaQueryResult {
    destineringalternativManuella: DestineringsalternativDTO[];
}

export class GetArbetsorderHandelssortimentQueryResult {
    arbetsorderHandelssortiment: ArbetsorderHandelssortimentDTO[];

}

export class GetArbetsorderAlertsQueryResult {
    arbetsorderId: number;
    namn: string;
    produktionsstatus: string;
    scheduledDate: Date;
    alerts: Alert[];
}

export class Alert {
    occured: Date;
    message: string;
    metadata: { [key: string]: string };
    severity: MessageSeverity;
    code: string;
    validationScope: number;
}

export enum Produktionsstatusar {
    produktionEjPaborjad = 1,
    produktionPagar = 2,
    produktionAvslutad = 3,
    produktionSlutrapporterad = 4
}
